/**
 * liveEntryLobbyViewModel — l'entrée dans le live en salle d'attente de visio : la barre, où en est
 * la séance, et le test du matériel au second plan (la rangée de chips, sa phrase, son geste, le mot
 * d'un appareil en défaut, l'image à monter).
 *
 * Pur : il lit la séance (`UILiveSession`), ce que la salle voit déjà (`tutorHere`), l'horloge, et la
 * vue du test traduite par `deviceCheckViewModel`. Les gestes restent au crochet du moteur ; l'écran
 * les branche sur ce que ce view-model nomme (`action`, `retry`).
 *
 * Les mots du test sont ceux de `<DeviceCheckPanel>`, qu'il remplace sur cet écran (« Vérifier mon
 * matériel », « Caméra et micro déjà vérifiés », « Revérifier », « Tout est prêt », « Il reste … à
 * régler », « Tu peux entrer quand même… »).
 */
import { formatHour } from "../../../ui/formatters/dateFormatter"
import { deviceCheckLineFailing } from "../../../ui/formatters/deviceCheckFormatter"
import { sessionCountdown, subjectSummary } from "../../../ui/formatters/liveSessionFormatter"
import { type UILiveSession } from "../../../ui/types"
import { type UIDeviceCheckLine, type UIDeviceCheckStepId } from "../../../ui/types/studentDeviceCheck"
import { type DeviceCheckVM } from "./deviceCheckViewModel"

export type LiveEntryHeadingVM = { title: string; caption: string }

export type LiveEntryStatusVM = {
    text: string
    detail?: string
    tutor?: UILiveSession["tutor"]
    tutorHere?: boolean
}

export type LiveEntryEquipmentVM = {
    /** Les chips. Absentes : le matériel est déjà vérifié, ou la séance pas encore lue. */
    lines?: UIDeviceCheckLine[]
    note?: string
    /** Le geste à la hauteur des chips : lancer le test, ou le refaire. */
    action: { kind: "start" | "recheck"; label: string } | null
    pending: boolean
    /** Le test fini avec des points en défaut : ses chips pressent, le mot invite à réessayer. */
    retryable: boolean
    notice: { text: string; tone: "warning"; actionLabel: string; retry: UIDeviceCheckStepId } | null
    /**
     * Le flux de la caméra se monte dans la grande image là où l'ancien écran montait son aperçu
     * (l'étape caméra, la carte « déjà vérifié »), et au bilan. Jamais pendant les sondes des étapes
     * d'avant : deux captures sur une webcam qui n'en veut qu'une donnent le « périphérique occupé ».
     */
    feedMounted: boolean
    /** La caméra n'est pas encore à l'écran pendant le test : ce que la grande image dit à la place. */
    cameraWaiting?: string
    /** La caméra est refusée ou ne s'ouvre pas : le point sur son bouton. */
    cameraProblem: boolean
    /** Le micro est refusé ou ne s'ouvre pas : le point sur son bouton, de même. */
    micProblem: boolean
    /** Une connexion faible, sur l'image. */
    network?: string
}

const TEXTS = {
    doing: "Prépare-toi avant d'entrer",
    unknownTitle: "Ton live",
    liveAt: (hour: string) => `Live de ${hour}`,
    withTutor: (firstname: string) => `avec ${firstname}`,
    here: (firstname: string) => `${firstname} est déjà là`,
    startsAt: (hour: string) => `Ton live commence à ${hour}`,
    early: (countdown: string) =>
        `${countdown.charAt(0).toUpperCase()}${countdown.slice(1)}. Tu peux entrer dès maintenant.`,
    startedAt: (hour: string) => `Ton live a commencé à ${hour}`,
    start: "Vérifier mon matériel",
    remembered: "Caméra et micro déjà vérifiés",
    recheck: "Revérifier",
    ready: "Tout est prêt",
    trouble: (count: number) => (count > 1 ? "Il reste quelques points à régler" : "Il reste un point à régler"),
    troubleText: "Tu peux entrer quand même. Tu pourras réessayer pendant le live.",
    retry: "Réessayer",
    cameraSoon: "On vérifie ta caméra juste après",
    joinPreparing: "On prépare la vérification…",
    joinNotStarted: "Ton matériel n'est pas encore vérifié",
    joinChecking: "On vérifie ton matériel…",
    joinDuring: (done: number, total: number) => `Vérification en cours : ${done} sur ${total}`,
}

const SEPARATOR = " · "

export type LiveEntryJoinVM = {
    /** Le test est fini, ou déjà fait sur cet appareil : « Rejoindre » passe en principal. */
    ready: boolean
    /** Le test n'est pas lancé (`report.started` faux) : l'entrée « sans vérifier » de l'ancien écran. */
    beforeCheck: boolean
    /** L'état du test, dit au-dessus de « Rejoindre », au téléphone. */
    note: string
}

/**
 * Ce que le pied dit du test, au-dessus de « Rejoindre » (Wajih, daily du 21/10 : la vérification
 * d'abord). L'entrée reste ouverte à tout instant : ce n'est qu'une hiérarchie, `ready` suit l'entrée
 * de l'ancien écran (`report.allDone` : son bouton plein, le lien gris avant), `beforeCheck` son libellé
 * d'avant le test (`check.started` faux : « Entrer sans vérifier », puis « Entrer sans finir »).
 */
export function liveEntryJoin(vm: DeviceCheckVM | null): LiveEntryJoinVM {
    if (vm == null) return { ready: false, beforeCheck: true, note: TEXTS.joinPreparing }
    return { ready: vm.entry === "ready", beforeCheck: vm.entry === "before", note: joinNote(vm) }
}

function joinNote(vm: DeviceCheckVM): string {
    const ready = vm.entry === "ready"
    if (vm.phase === "remembered") return ready ? TEXTS.remembered : TEXTS.joinChecking
    if (vm.phase === "intro") return vm.starting ? TEXTS.joinChecking : TEXTS.joinNotStarted
    if (vm.currentIndex != null) {
        const done = vm.lines.filter((line) => line.verdict === "ok" && line.skipped !== true).length
        return TEXTS.joinDuring(done, vm.lines.length)
    }
    const left = vm.lines.filter(deviceCheckLineFailing).length
    return left === 0 ? TEXTS.ready : TEXTS.trouble(left)
}

/** La barre : « Mathématiques avec Camille », puis « Live de 18h00 · Prépare-toi avant d'entrer ». */
export function liveEntryHeading(session: UILiveSession | null): LiveEntryHeadingVM {
    if (session == null) return { title: TEXTS.unknownTitle, caption: TEXTS.doing }
    const subjects = subjectSummary(session.subjects)
    const withTutor = TEXTS.withTutor(session.tutor.firstname)
    return {
        title: subjects !== "" ? `${subjects} ${withTutor}` : `${TEXTS.unknownTitle} ${withTutor}`,
        caption: `${TEXTS.liveAt(formatHour(session.date))}${SEPARATOR}${TEXTS.doing}`,
    }
}

/**
 * Où en est la séance. Le masteur déjà là quand la salle le voit ; sinon l'heure : pas encore venue
 * (on peut entrer quand même, comme aujourd'hui), ou passée. D'ici, l'absence du masteur ne se sait
 * pas : la page ne la dit jamais.
 */
export function liveEntryStatus(
    session: UILiveSession | null,
    tutorHere: boolean,
    now: number = Date.now()
): LiveEntryStatusVM | undefined {
    if (session == null) return undefined
    const { tutor } = session
    if (tutorHere) return { tutor, tutorHere: true, text: TEXTS.here(tutor.firstname) }
    const start = liveStartStatus(session, now)
    return start != null ? { tutor, ...start } : undefined
}

/**
 * L'heure de la séance, dite à qui va y entrer : pas encore venue (« Ton live commence à 18h00 », et
 * combien de temps il reste — on peut entrer quand même), ou passée (« Ton live a commencé à 18h00 »).
 * Rien quand la séance n'est pas du jour (`sessionCountdown`). Les mots valent pour l'élève et le masteur.
 */
export function liveStartStatus(
    session: Pick<UILiveSession, "date" | "duration" | "statuses">,
    now: number = Date.now()
): { text: string; detail?: string } | undefined {
    const hour = formatHour(session.date)
    if (new Date(session.date).getTime() > now) {
        const countdown = sessionCountdown(session, now)
        return countdown != null ? { text: TEXTS.startsAt(hour), detail: TEXTS.early(countdown) } : undefined
    }
    return { text: TEXTS.startedAt(hour) }
}

/**
 * Le test du matériel au second plan. `vm` absent : la séance n'est pas encore lue, on ne sait pas
 * encore quel test servir — « On vérifie… ».
 */
export function liveEntryEquipment(vm: DeviceCheckVM | null): LiveEntryEquipmentVM {
    const none = {
        retryable: false,
        notice: null,
        feedMounted: false,
        cameraProblem: false,
        micProblem: false,
    }
    if (vm == null) return { ...none, action: null, pending: true }

    const camera = vm.lines.find((line) => line.id === "camera")
    const microphone = vm.lines.find((line) => line.id === "microphone")
    const network = vm.lines.find((line) => line.id === "network")
    const onCameraStep = vm.phase === "journey" && (vm.currentIndex == null || vm.step?.line.id === "camera")
    const feedMounted = !vm.preview.videoOff && (onCameraStep || vm.rememberedPreview)
    const device = {
        feedMounted,
        cameraWaiting: vm.phase === "journey" && !vm.preview.videoOff && !feedMounted ? TEXTS.cameraSoon : undefined,
        cameraProblem: camera?.verdict === "bad" && camera.skipped !== true,
        micProblem: microphone?.verdict === "bad" && microphone.skipped !== true,
        network:
            network != null && (network.verdict === "warn" || network.verdict === "bad") && network.detail !== ""
                ? network.detail
                : undefined,
    }

    if (vm.phase === "remembered") {
        return {
            ...none,
            ...device,
            note: TEXTS.remembered,
            pending: vm.rememberedPending,
            action: vm.canRecheck && !vm.rememberedPending ? { kind: "recheck", label: TEXTS.recheck } : null,
        }
    }

    if (vm.phase === "intro") {
        return {
            ...none,
            ...device,
            lines: vm.lines,
            note: vm.memoryMiss,
            pending: vm.starting,
            action: vm.starting ? null : { kind: "start", label: TEXTS.start },
        }
    }

    // Le parcours : une étape ouverte se lit dessous ; fini, le bilan se dit sur la rangée.
    if (vm.currentIndex != null) return { ...none, ...device, lines: vm.lines, action: null, pending: false }
    const left = vm.lines.filter(deviceCheckLineFailing)
    if (left.length === 0)
        return { ...none, ...device, lines: vm.lines, note: TEXTS.ready, action: null, pending: false }
    return {
        ...device,
        lines: vm.lines,
        note: TEXTS.trouble(left.length),
        action: null,
        pending: false,
        retryable: true,
        notice: { text: TEXTS.troubleText, tone: "warning", actionLabel: TEXTS.retry, retry: left[0]!.id },
    }
}
